import FlatfileImporter from "@flatfile/adapter";
import { toWSMoneyString } from "@wingspanhq/fe-component-library";
import { format, getYear, isFuture, isToday, isValid } from "date-fns";
import { useUserId } from "../../../../query/hooks/helpers";
import { useCollaboratorsQuery } from "../../../../query/payments/queries";
import { getVisibleCollaboratorsSortByCreatedAt } from "../../../../query/payments/selectors";
import { useUserProfile } from "../../../../query/users/queries";
import { convertDateToMidday } from "../../../../utils/dates";
import { validateEmail } from "../../../../utils/validators";
import { useWSAnalytics } from "../../../../utils/WSAnalytics";
import { PayableStatus, UploadPayable } from "./BulkUploadPayablesInfo";
import { flatfileTheme } from "../../../../utils/flatfileTheme";
import { getMatchingCollaborators } from "../../bulkUploadCollaborators/collaboratorUtils";
import { FlatfileImporterV2Options } from "../../../../flatfile/types";
import { getCurrentFilingYear } from "../../../../1099NECFiling/utils";

const apiKey = process.env.REACT_APP_FLATFILE_API_KEY || "";

export const useBulkUploadPayablesImporter = (
  options?: FlatfileImporterV2Options
) => {
  const userId = useUserId();
  const user = useUserProfile(userId);
  const collaboratorsQuery = useCollaboratorsQuery();
  const existingCollaborators = getVisibleCollaboratorsSortByCreatedAt(
    collaboratorsQuery.data || []
  );
  const { track } = useWSAnalytics();

  const importer = new FlatfileImporter(apiKey, {
    ...flatfileTheme,
    type: "Payables",
    title: "Bulk upload payables to Wingspan",
    autoDetectHeaders: true,
    devMode: !(process.env.REACT_APP_ENV === "production"),
    managed: true,
    disableManualInput: true,
    dateFormat: "mm/dd/yyyy",
    displayEncoding: false,
    allowCustom: true,
    fields: [
      {
        label: "Email",
        key: "email",
        validators: [
          {
            validate: "required_without_all",
            fields: ["collaboratorId", "contractorId"]
          }
        ]
      },
      {
        label: "Collaborator ID",
        key: "collaboratorId",
        validators: [
          {
            validate: "required_without_all",
            fields: ["email", "contractorId"]
          }
        ]
      },
      {
        label: "External Contractor ID",
        key: "contractorId",
        alternates: ["Contractor ID"],
        validators: [
          {
            validate: "required_without_all",
            fields: ["email", "collaboratorId"]
          }
        ]
      },
      {
        label: "Amount (USD)",
        key: "amount",
        alternates: ["Amount"]
      },
      {
        label: "Line Item Title",
        key: "lineItemTitle",
        validators: [{ validate: "required" }]
      },
      {
        label: "Line Item Description",
        key: "lineItemDescription"
      },
      {
        label: "Line Item Hourly Rate",
        key: "lineItemHourlyRate"
      },
      {
        label: "Line Item Hours",
        key: "lineItemHours"
      },
      {
        label: "Reimbursable",
        key: "reimbursable",
        type: "checkbox"
      },
      {
        label: "Due Date",
        key: "dueDate"
      },
      {
        label: "Invoice Notes",
        key: "invoiceNotes"
      },
      {
        label: "Paid Date",
        key: "paidDate"
      }
    ]
  });

  importer.registerRecordHook(async (record: any) => {
    const matchingCollaborators = getMatchingCollaborators(
      existingCollaborators,
      {
        email: record.email,
        collaboratorId: record.collaboratorId,
        contractorId: record.contractorId
      }
    );
    const output: any = {};
    if (record.email) {
      const isValidEmail = validateEmail(record.email);
      if (!isValidEmail) {
        output.email = {
          value: record.email,
          info: [
            {
              message: "Invalid email address",
              level: "error"
            }
          ]
        };
      } else {
        output.email = {
          value: record.email.toLowerCase()
        };
      }

      if (record.collaboratorId || record.contractorId) {
        output.email = {
          value: record.email.toLowerCase(),
          info: [
            {
              message:
                "Please include only one collaborator identifier: email or collaborator ID or contractor ID",
              level: "error"
            }
          ]
        };
      }
    }
    if (record.collaboratorId) {
      if (record.email || record.contractorId) {
        output.collaboratorId = {
          value: record.collaboratorId,
          info: [
            {
              message:
                "Please include only one collaborator identifier: email or collaborator ID or contractor ID",
              level: "error"
            }
          ]
        };
      }
    }
    if (record.contractorId) {
      if (record.email || record.collaboratorId) {
        output.contractorId = {
          value: record.contractorId,
          info: [
            {
              message:
                "Please include only one collaborator identifier: email or collaborator ID or contractor ID",
              level: "error"
            }
          ]
        };
      }
    }

    if (matchingCollaborators.length === 0) {
      // If no collaborators found
      const errorObj = {
        info: [
          {
            message:
              "This collaborator does not exist in Wingspan. Please invite them first before making a payment",
            level: "error"
          }
        ]
      };
      if (record.email) {
        output.email = {
          ...errorObj,
          value: record.email
        };
      }
      if (record.collaboratorId) {
        output.collaboratorId = {
          ...errorObj,
          value: record.collaboratorId
        };
      }
      if (record.contractorId) {
        output.contractorId = {
          ...errorObj,
          value: record.contractorId
        };
      }
    }

    if (matchingCollaborators.length >= 2) {
      // If multiple collaborators found
      if (record.email) {
        output.email = {
          info: [
            {
              message:
                "Multiple collaborators exist with this email. Payable will be created for the most recently created collaborator.",
              level: "info"
            }
          ],
          value: record.email.toLowerCase()
        };
      }
      if (record.contractorId) {
        output.contractorId = {
          info: [
            {
              message:
                "Multiple collaborators exist with this contractor ID. Payable will be created for the most recently created collaborator.",
              level: "info"
            }
          ],
          value: record.contractorId
        };
      }
    }

    // Format and validate total amount. It could be taken from Amount field OR combination of Hourly rate and Hours fields.

    const amount = record.amount
      ? Number(record.amount.replace(/[^0-9.-]+/g, ""))
      : 0;
    const hourlyRate = record.lineItemHourlyRate
      ? Number(record.lineItemHourlyRate.replace(/[^0-9.-]+/g, ""))
      : 0;
    const hours = record.lineItemHours
      ? Number(record.lineItemHours.replace(/[^0-9.-]+/g, ""))
      : 0;

    if (isNaN(amount)) {
      output.amount = {
        value: record.amount,
        info: [
          {
            message: "Please enter valid amount",
            level: "error"
          }
        ]
      };
    } else {
      const formattedValue = toWSMoneyString(amount).replace("$", "");

      if (record.amount) {
        output.amount = {
          value: formattedValue,
          info: [
            {
              message: "Amount formatted",
              level: "info"
            }
          ]
        };
      }
    }

    if (isNaN(hourlyRate)) {
      output.lineItemHourlyRate = {
        value: record.lineItemHourlyRate,
        info: [
          {
            message: "Please enter valid hourly rate",
            level: "error"
          }
        ]
      };
    } else {
      const formattedValue = toWSMoneyString(hourlyRate).replace("$", "");

      output.lineItemHourlyRate = {
        value: formattedValue,
        info: [
          {
            message: "Hourly rate formatted",
            level: "info"
          }
        ]
      };
    }

    if (isNaN(hours)) {
      output.lineItemHours = {
        value: record.lineItemHours,
        info: [
          {
            message: "Please enter valid hours",
            level: "error"
          }
        ]
      };
    } else {
      const formattedValue = toWSMoneyString(hours).replace("$", "");

      output.lineItemHours = {
        value: formattedValue,
        info: [
          {
            message: "Hours formatted",
            level: "info"
          }
        ]
      };
    }

    if (record.dueDate) {
      // Validate and reformat Due Date
      const dueDate = convertDateToMidday(new Date(record.dueDate));
      if (isValid(dueDate)) {
        const formattedDate = format(dueDate, "yyyy-MM-dd");
        const isInvalidDueDate =
          options?.payableStatus === PayableStatus.Paid
            ? // TODO: Do we have to check if there is any date which is in 2023?
              false
            : !(isToday(dueDate) || isFuture(dueDate));
        output.dueDate = {
          value: formattedDate,
          info: isInvalidDueDate
            ? [
                {
                  message: "Date cannot be in the past.",
                  level: "error"
                }
              ]
            : []
        };
      } else {
        output.dueDate = {
          value: record.dueDate,
          info: [
            {
              message: "Please provide a valid due date.",
              level: "error"
            }
          ]
        };
      }
    } else {
      if (options?.payableStatus !== PayableStatus.Paid) {
        output.dueDate = {
          value: format(new Date(), "yyyy-MM-dd"),
          info: [
            {
              message: "Due date automatically set",
              level: "info"
            }
          ]
        };
      }
    }

    if (record.paidDate) {
      // Validate and reformat Paid Date
      const paidDate = convertDateToMidday(new Date(record.paidDate));
      if (isValid(paidDate)) {
        const currentFilingYear = getCurrentFilingYear();
        output.paidDate = {
          value: format(paidDate, "yyyy-MM-dd"),
          info:
            getYear(paidDate) !== currentFilingYear
              ? [
                  {
                    message: `Include payments of ${currentFilingYear} only`,
                    level: "error"
                  }
                ]
              : []
        };
      } else {
        output.paidDate = {
          value: record.paidDate,
          info: [
            {
              message: "Please provide a valid paid date.",
              level: "error"
            }
          ]
        };
      }
    }

    if (
      options?.payableStatus === PayableStatus.Paid &&
      !isNaN(amount) &&
      amount < 0
    ) {
      output.amount = {
        value: record.amount,
        info: [
          {
            message:
              "This record can not be imported because it has negative amount",
            level: "warning"
          }
        ]
      };
    }

    // Replace \n with line break

    if (record.lineItemTitle) {
      output.lineItemTitle = {
        value: record.lineItemTitle.replace("\\n", "\n")
      };
    }

    if (record.lineItemDescription) {
      output.lineItemDescription = {
        value: record.lineItemDescription.replace("\\n", "\n")
      };
    }

    if (record.invoiceNotes) {
      output.invoiceNotes = {
        value: record.invoiceNotes.replace("\\n", "\n")
      };
    }

    return output;
  });

  importer.setCustomer({
    userId: userId,
    name: `${user.data?.profile.firstName} ${user.data?.profile.lastName}`
  });

  importer.registerStepHook("upload", data => {
    track.other("Flatfile Event Occurred", {
      batchId: data.batchId,
      event: "upload",
      type: "Payables"
    });

    return true;
  });

  importer.registerStepHook("match", data => {
    track.other("Flatfile Event Occurred", {
      batchId: data.batchId,
      event: "match",
      type: "Payables"
    });

    return true;
  });

  importer.registerStepHook("review", data => {
    if (!data.headers_matched?.find(v => v.matched_key === "dueDate")) {
      importer.addVirtualField({
        label: "Due date",
        key: "dueDate"
      });
    }

    track.other("Flatfile Event Occurred", {
      batchId: data.batchId,
      event: "review",
      type: "Payables"
    });

    return true;
  });

  importer.on("close", () => {
    options?.onClose?.();
  });

  return async () => {
    try {
      const results = await importer.requestDataFromUser();
      await importer.close();
      return results;
    } catch (error) {
      console.info(error || "window close");
      throw error;
    }
  };
};
